import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '📗',
  number: 'MODULE 1',
  title: 'Speaking & Conversation',
  subtitle: 'The module that finally makes your students talk',
  problem: 'The number one complaint Spanish teachers hear is: "My students won\'t speak." This module changes that — by giving students real reasons to talk and real structures to say something meaningful from day one.',
  bullets: ['Greeting and introduction scripts ready for Day 1', 'Conversation question cards for pair and group work', 'Interactive oral activities with clear facilitation guides', 'Role-play scenarios for real-world situations', 'Discussion prompts connected to authentic cultural topics'],
  result: 'Your students go from staring at their desks to raising their hands — because they finally have something real to say and the structures to say it confidently.'
}, {
  icon: '📘',
  number: 'MODULE 2',
  title: 'Reading & Writing',
  subtitle: 'The module that builds real literacy in Spanish',
  problem: 'Most reading activities in Spanish class feel fake. Students know the texts were invented for a grammar exercise. This module uses authentic-feeling, culturally rich texts that students actually want to read — and writing scaffolds that take students from one sentence to full paragraphs.',
  bullets: ['Original reading comprehension texts at multiple levels', 'Pre-reading vocabulary activators and cultural context builders', 'Comprehension questions at literal and inferential levels', 'Guided writing templates with sentence frames for every level', 'Paragraph construction activities and creative writing prompts', 'Peer review tools with clear, student-friendly criteria'],
  result: 'Your students develop real reading stamina and writing confidence in Spanish — and discover that expressing themselves in another language is genuinely possible.'
}, {
  icon: '📙',
  number: 'MODULE 3',
  title: 'Listening & Vocabulary',
  subtitle: 'The module that builds the skills students actually need outside the classroom',
  problem: 'Listening is the most neglected skill in most Spanish classrooms — because creating good listening activities takes enormous time. And vocabulary taught in isolation is vocabulary forgotten by Friday. This module solves both problems at once.',
  bullets: ['Listening activities with complete transcripts and facilitation guides', 'Cultural listening units featuring real-world Spanish-language content', '12 thematic vocabulary units embedded in real communicative contexts', 'Self-correction answer keys for every activity'],
  result: 'Your students build a rich vocabulary base they can actually activate in real conversation — and develop real listening comprehension that works outside the classroom walls.',
  extraTable: {
    title: 'Themes Covered:',
    rows: [['Unit 1', 'Greetings and Personal Information'], ['Unit 2', 'Family and Relationships'], ['Unit 3', 'School and Classroom Life'], ['Unit 4', 'Daily Routines'], ['Unit 5', 'Food and Drinks'], ['Unit 6', 'Clothing and Shopping'], ['Unit 7', 'Home and City'], ['Unit 8', 'Travel and Transportation'], ['Unit 9', 'Weather and Seasons'], ['Unit 10', 'Health and Body'], ['Unit 11', 'Technology and Social Media'], ['Unit 12', 'Culture and Traditions']]
  }
}, {
  icon: '📕',
  number: 'MODULE 4',
  title: 'Grammar — Progressive and In Context',
  subtitle: 'The module that finally makes grammar stick',
  problem: 'Grammar does not have to be boring. Every grammar unit in this module introduces structures through real language use — so students acquire grammar patterns rather than memorizing rules they forget the following Monday.',
  bullets: ['Grammar lessons organized from foundations through intermediate structures', 'Every grammar point taught in context — never as an isolated rule', 'Practice activities that require real communication, not just blank-filling', 'Clear explanations designed for English-speaking learners of Spanish', 'Complete answer keys for every grammar activity'],
  result: 'Your students stop confusing core structures and start using grammar as a tool for real communication — not a list of rules to memorize and immediately forget.',
  popular: true,
  extraTable: {
    title: 'Grammar Sequence Included:',
    rows: [['Foundations', 'Pronouns, gender and number, articles, adjective agreement'], ['Core Structures', 'Present tense, ser vs. estar, tener and hay, gustar, reflexive verbs'], ['Communication Builders', 'Questions, negations, present progressive'], ['Narrative Tenses', 'Preterite, preterite vs. imperfect contrast'], ['Advanced Introduction', 'Introduction to the subjunctive']]
  }
}, {
  icon: '📓',
  number: 'MODULE 5',
  title: 'Cultural Immersion & Projects',
  subtitle: 'The module that makes Spanish feel real and worth learning',
  problem: 'Most Spanish classes reduce an entire civilization to tamales and Frida Kahlo. This module brings the real Spanish-speaking world into your classroom — contemporary, diverse, and genuinely interesting to your students.',
  bullets: ['Cultural lessons covering multiple Spanish-speaking countries', 'Heritage culture connection activities for heritage speaker students', 'Contemporary culture units — music, food, art, and social movements', 'Classroom games requiring zero technology', 'Group project templates with clear rubrics and facilitation guides', 'Role-play scenarios for realistic real-world communication practice', 'Printable game materials — ready to cut and use immediately'],
  result: 'Your heritage speakers feel seen and represented. Your non-heritage students discover that Spanish is not foreign — it is woven into the world around them. And your reluctant learners become participants because they finally have a reason to care.'
}, {
  icon: '📔',
  number: 'MODULE 6',
  title: 'Assessments, Rubrics & Emergency Lessons',
  subtitle: 'The module that gives you your weekends back',
  problem: "Grading 150 assessments on a Sunday is not teaching. It is suffering. And waking up sick with no substitute plan is every teacher's nightmare. This module eliminates both problems permanently.",
  bullets: ['Oral assessment tools with clear, time-saving scoring criteria', 'Written assessments at multiple difficulty levels for every unit', 'Quick formative assessment activities for every lesson', 'AI-resistant assessment formats focused on authentic communication', 'Complete answer keys for every single assessment', 'No-prep emergency lessons for any Spanish level', 'Substitute teacher packages any sub can run independently', 'Five-minute activity bank for unexpected schedule gaps', 'Printable tools and classroom display materials'],
  result: 'You assess with confidence and consistency. You always have something ready for emergencies. And you stop spending your Sundays doing work that a better system can do for you.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-cream" data-id="element-278">
      <div className="max-w-6xl mx-auto" data-id="element-279">
        <div className="text-center mb-16" data-id="element-280">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-navy" data-id="element-281">
            📘 The 6-in-1 Spanish Ready™ System
          </h2>
          <p className="text-xl text-turquoise font-medium" data-id="element-282">
            Six complete modules. Every skill. Every level. Every situation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-283">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-white rounded-2xl border ${tomo.popular ? 'border-gold shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-navy/10 shadow-sm'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-284">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-gold text-white font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-285">
                  MOST POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-286">
                <span className="text-4xl" data-id="element-287">{tomo.icon}</span>
                <div data-id="element-288">
                  <div className="text-turquoise font-bold text-sm uppercase tracking-wider" data-id="element-289">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1 text-navy" data-id="element-290">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-navy-light font-medium mb-6 italic border-l-2 border-turquoise/50 pl-4" data-id="element-291">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-292">
                <h4 className="text-sm font-bold text-navy uppercase tracking-wider mb-2" data-id="element-293">
                  What it solves in the classroom:
                </h4>
                <p className="text-navy-light text-sm leading-relaxed" data-id="element-294">
                  {tomo.problem}
                </p>
              </div>

              <div className="mb-6" data-id="element-295">
                <h4 className="text-sm font-bold text-navy uppercase tracking-wider mb-3" data-id="element-296">
                  What is included:
                </h4>
                <ul className="space-y-3" data-id="element-297">
                  {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-298">
                      <CheckCircle2 size={18} className="text-turquoise shrink-0 mt-0.5" data-id="element-299" />
                      <span className="text-navy-light text-sm" data-id="element-300">{bullet}</span>
                    </li>)}
                </ul>
              </div>

              {tomo.extraTable && <div className="mb-8" data-id="element-301">
                  <h4 className="text-sm font-bold text-navy uppercase tracking-wider mb-3" data-id="element-302">
                    {tomo.extraTable.title}
                  </h4>
                  <div className="bg-cream rounded-lg border border-navy/5 overflow-hidden text-sm" data-id="element-303">
                    {tomo.extraTable.rows.map((row, i) => <div key={i} className={`flex border-b border-navy/5 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-cream/50'}`} data-id="element-304">
                        <div className="w-1/3 p-2 font-medium text-navy border-r border-navy/5" data-id="element-305">
                          {row[0]}
                        </div>
                        <div className="w-2/3 p-2 text-navy-light" data-id="element-306">
                          {row[1]}
                        </div>
                      </div>)}
                  </div>
                </div>}

              <div className="bg-turquoise/10 border border-turquoise/30 rounded-xl p-4 mt-auto" data-id="element-307">
                <span className="text-turquoise font-bold block mb-1" data-id="element-308">
                  Result:
                </span>
                <p className="text-navy text-sm" data-id="element-309">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>

        {/* Extra Tables Section */}
        <div className="mt-20 grid md:grid-cols-2 gap-8" data-id="element-310">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-2xl border border-navy/10 shadow-sm" data-id="element-311">
            <h3 className="text-2xl font-serif font-bold text-navy mb-6" data-id="element-312">
              How You Can Use Spanish Ready™
            </h3>
            <div className="space-y-3 text-sm" data-id="element-313">
              {[['Complete lessons', 'Full lesson plans ready to deliver from start to finish'], ['Warm-up activities', 'Quick-start activities for the first 5 minutes of class'], ['Oral practice', 'Speaking activities that generate real conversation'], ['Review sessions', 'Review games requiring zero preparation'], ['Homework', 'Independent practice with complete answer keys'], ['Assessments', 'Formal and informal tools for every unit'], ['Last-minute lessons', 'Emergency lessons ready when you have no time to plan'], ['Tutoring', 'One-on-one activities that work without a full class'], ['Online classes', 'Digital-ready activities for virtual teaching environments'], ['Early finishers', 'Extension activities for students who finish ahead'], ['Substitute days', 'Sub-friendly packages any substitute can manage independently']].map((row, i) => <div key={i} className="flex flex-col sm:flex-row gap-1 sm:gap-4 border-b border-navy/5 pb-3 last:border-0 last:pb-0" data-id="element-314">
                  <div className="sm:w-1/3 font-bold text-turquoise" data-id="element-315">
                    {row[0]}
                  </div>
                  <div className="sm:w-2/3 text-navy-light" data-id="element-316">{row[1]}</div>
                </div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-2xl border border-navy/10 shadow-sm" data-id="element-317">
            <h3 className="text-2xl font-serif font-bold text-navy mb-6" data-id="element-318">
              Ideal For
            </h3>
            <div className="space-y-4 text-sm" data-id="element-319">
              {[['Spanish 1 and Spanish 2', 'Full coverage of beginner and early intermediate content'], ['Middle School', 'Age-appropriate activities and culturally relevant content'], ['High School', 'Rigorous, engaging content for teenage learners'], ['Private Tutoring', 'Flexible activities that work one-on-one'], ['Language Academies', 'Professional-quality materials for institutional use'], ['Homeschool', 'Complete curriculum support for home educators'], ['In-person and Online', 'Every activity works in both teaching environments']].map((row, i) => <div key={i} className="flex flex-col sm:flex-row gap-1 sm:gap-4 border-b border-navy/5 pb-4 last:border-0 last:pb-0" data-id="element-320">
                  <div className="sm:w-1/3 font-bold text-navy" data-id="element-321">{row[0]}</div>
                  <div className="sm:w-2/3 text-navy-light" data-id="element-322">{row[1]}</div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}